import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import { BridgeDownloadCard } from './BridgeDownloadCard';
// LA-12: one intake component (LA-11) mounted inside the EXISTING Local Node block.
// No second catalog, no second install path (LA-13/LA-19).
import { LocalAiIntake, dispatchLocalAiPlan } from './LocalAiIntake';
import { DeviceGrantsPanel } from '../Team/DeviceGrantsPanel';
import { safeTeamLabel } from '../../api/team';
import {
  fetchDeviceCapabilities,
  fetchDevices,
  fetchNodeOperatingModes,
  revokeDevice,
  updateNodeOperatingModes,
} from '../../api/operations';
import type {
  DeviceCapability,
  DeviceListResponse,
  ExecutionPreference,
  NodeOperatingModes,
} from '../../api/operations';

const EXECUTION_PREFERENCES: { value: ExecutionPreference; label: string }[] = [
  { value: 'local_only', label: 'Local only' },
  { value: 'prefer_local', label: 'Prefer Local Node' },
  { value: 'either', label: 'Local Node or cloud' },
  { value: 'cloud_allowed', label: 'Cloud allowed' },
];

const NODE_ONLINE_WINDOW_MS = 90 * 1000;

function deviceOnline(lastSeenAt: string | null, now: number): boolean {
  if (!lastSeenAt) return false;
  const seen = Date.parse(lastSeenAt);
  return Number.isFinite(seen) && seen <= now && now - seen < NODE_ONLINE_WINDOW_MS;
}

export function LocalNodePanel() {
  const [devices, setDevices] = useState<DeviceListResponse | null>(null);
  const [modes, setModes] = useState<NodeOperatingModes | null>(null);
  const [capabilities, setCapabilities] = useState<Record<string, DeviceCapability[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [teamExpanded, setTeamExpanded] = useState<Record<string, boolean>>({});
  const [busyDevice, setBusyDevice] = useState('');
  const [busyMode, setBusyMode] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [deviceResponse, modeResponse] = await Promise.all([
        fetchDevices(),
        fetchNodeOperatingModes(),
      ]);
      setDevices(deviceResponse);
      setModes(modeResponse);
    } catch (failure) {
      setDevices(null);
      setCapabilities({});
      setError(failure instanceof Error ? failure.message : 'Local Node settings could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function toggleCapabilities(deviceId: string) {
    const next = !expanded[deviceId];
    setExpanded((current) => ({ ...current, [deviceId]: next }));
    if (!next || capabilities[deviceId]) return;
    await loadCapabilities(deviceId);
  }

  /** LA-12: the Rescan action re-reads the device capabilities that already exist. No new route. */
  async function loadCapabilities(deviceId: string): Promise<boolean> {
    try {
      const response = await fetchDeviceCapabilities(deviceId);
      setCapabilities((current) => ({ ...current, [deviceId]: response.capabilities || [] }));
      setError('');
      return true;
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Device capabilities could not be loaded.');
      return false;
    }
  }

  /** LA-12: Rescan re-reads this device's capabilities; pairing, revoke and execution preference are untouched. */
  async function rescanCapabilities(deviceId: string) {
    setError('');
    setNotice('');
    const rescanned = await loadCapabilities(deviceId);
    setNotice(rescanned ? 'Local Node capabilities rescanned.' : 'This Local Node could not be scanned.');
  }

  async function confirmRevoke(deviceId: string, label: string) {
    if (!window.confirm('Revoke ' + label + '? Cloud access for this Local Node will end immediately.')) return;
    setError('');
    setNotice('');
    setBusyDevice(deviceId);
    try {
      await revokeDevice(deviceId);
      setNotice('Local Node revoked.');
      await refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The Local Node could not be revoked.');
    } finally {
      setBusyDevice('');
    }
  }

  async function changeExecutionPreference(value: ExecutionPreference) {
    setError('');
    setNotice('');
    setBusyMode(true);
    try {
      const updated = await updateNodeOperatingModes({ execution_preference: value });
      setModes(updated);
      setNotice('Execution preference saved.');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The execution preference could not be saved.');
    } finally {
      setBusyMode(false);
    }
  }

  return (
    <section className="settings-panel-content">
      <BridgeDownloadCard />
      <div className="settings-card-heading">
        <div>
          <h3>Local Node</h3>
          <p>Pair a workstation to use its official CLI sessions and declared capabilities.</p>
        </div>
        <Link className="settings-link-button" to="/devices/pair">Pair a Local Node</Link>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p className="success" role="status">{notice}</p> : null}

      <section className="settings-subsection">
        <h4>Paired devices</h4>
        {devices === null ? (
          <p role="status">{error ? 'Local Node status is unavailable.' : 'Loading Local Nodes…'}</p>
        ) : devices.devices.length === 0 ? (
          <p>No Local Nodes are paired yet. <Link to="/devices/pair">Start pairing</Link>.</p>
        ) : (
          <div className="settings-card-list">
            {devices.devices.map((device) => (
              <article className="settings-card" key={device.device_id}>
                <div className="settings-card-heading">
                  <div>
                    <h5>{safeTeamLabel(device.label, 'Local Node')}</h5>
                    <p className="settings-muted">
                      {device.connection_state === 'connected' && deviceOnline(device.last_seen_at, now) ? 'Connected' : 'Offline'}
                      {' · '}{device.status}
                      {device.last_seen_at ? ' · Last seen ' + new Date(device.last_seen_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <div className="settings-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => void toggleCapabilities(device.device_id)}
                    >
                      {expanded[device.device_id] ? 'Hide capabilities' : 'Show capabilities'}
                    </button>
                    {device.status !== 'revoked' ? (
                      <button
                        type="button"
                        className="danger-button"
                        disabled={busyDevice === device.device_id}
                        onClick={() => void confirmRevoke(device.device_id, safeTeamLabel(device.label, 'Local Node'))}
                      >
                        {busyDevice === device.device_id ? 'Revoking…' : 'Revoke'}
                      </button>
                    ) : null}
                  </div>
                </div>
                <details onToggle={(event) => {
                  const open = event.currentTarget.open;
                  setTeamExpanded((current) => ({ ...current, [device.device_id]: open }));
                }}>
                  <summary>Team access</summary>
                  {teamExpanded[device.device_id] ? <DeviceGrantsPanel deviceId={device.device_id} /> : null}
                </details>
                {expanded[device.device_id] ? (
                  <div className="settings-capabilities">
                    {!capabilities[device.device_id] ? (
                      <p role="status">Loading capabilities…</p>
                    ) : capabilities[device.device_id].length === 0 ? (
                      <p>No capabilities were reported by this device.</p>
                    ) : (
                      <ul>
                        {capabilities[device.device_id].map((capability) => (
                          <li key={capability.capability_id}>
                            <strong>{capability.capability_id}</strong>
                            {' · '}{capability.group} · {capability.health} · version {capability.version}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
                {/* LA-12: the Local AI intake for this device, inside the existing Local Node block.
                    A revoked node has no cloud access left, so it gets no intake and no Rescan. */}
                {device.status !== 'revoked' ? (
                  <div className="settings-subsection">
                    <div className="settings-card-heading">
                      <div>
                        <h5>Set up local AI</h5>
                        <p className="settings-muted">
                          Install the local models Auteur has tested onto this computer, and see the
                          state of your local tools.
                        </p>
                      </div>
                      <div className="settings-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => void rescanCapabilities(device.device_id)}
                        >
                          Rescan
                        </button>
                      </div>
                    </div>
                    <LocalAiIntake mode="settings" deviceId={device.device_id} onDownloadPlan={dispatchLocalAiPlan} />
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="settings-subsection">
        <h4>Execution preference</h4>
        {modes ? (
          <>
            <label htmlFor="node-execution-preference">Where Auteur should prefer to run work</label>
            <select
              id="node-execution-preference"
              value={modes.execution_preference}
              disabled={busyMode}
              onChange={(event) => void changeExecutionPreference(event.target.value as ExecutionPreference)}
            >
              {EXECUTION_PREFERENCES.map((preference) => (
                <option key={preference.value} value={preference.value}>{preference.label}</option>
              ))}
            </select>
            <p className="settings-muted">
              Current source: {modes.execution_preference_source}. The scheduler
              {modes.enforced_by_scheduler ? ' enforces this preference.' : ' does not enforce this preference yet.'}
            </p>
          </>
        ) : (
          <p>Execution preferences are not available until the mode settings load.</p>
        )}
      </section>
    </section>
  );
}
